import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqData } from '../mockData';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const FAQ = () => {
  const { t, language } = useLanguage();

  const additionalFaqsEn = [
    {
      question: 'What countries do you recruit from?',
      answer: 'We primarily recruit from the UK, Europe, Australia, Canada, and other Western countries. We also work with qualified professionals from various regions globally who meet Saudi healthcare standards.'
    },
    {
      question: 'How does the video interview work?',
      answer: 'Candidates complete a short video interview answering standard questions. Our tools help assess communication skills, confidence, and professionalism. Employers can review the video before scheduling formal interviews.'
    },
    {
      question: 'What if a hire doesn\'t work out?',
      answer: 'If a candidate leaves within the first 30 days, we will aim to provide a replacement at no additional cost, subject to plan terms and conditions. Contact our team for specific details about your plan.'
    },
    {
      question: 'How secure is candidate data?',
      answer: 'We use encryption and comply with UK GDPR and Saudi data protection laws. All data is stored securely and never shared without explicit consent.'
    },
    {
      question: 'Can I post jobs in Arabic?',
      answer: 'Yes! Our platform supports both English and Arabic. You can post jobs in either language or both.'
    },
    {
      question: 'What support do candidates receive for relocation?',
      answer: 'Candidates receive support including SCFHS registration guidance, visa processing assistance, and coordination help for accommodation arrangements and cultural orientation for working in Saudi Arabia.'
    }
  ];

  const additionalFaqsAr = [
    {
      question: 'من أي دول تقومون بالتوظيف؟',
      answer: 'نوظف بشكل رئيسي من المملكة المتحدة وأوروبا وأستراليا وكندا ودول غربية أخرى. نعمل أيضاً مع متخصصين مؤهلين من مناطق مختلفة حول العالم ممن يستوفون معايير الرعاية الصحية السعودية.'
    },
    {
      question: 'كيف تعمل مقابلة الفيديو؟',
      answer: 'يكمل المرشحون مقابلة فيديو قصيرة للإجابة على أسئلة قياسية. تساعد أدواتنا في تقييم مهارات التواصل والثقة والاحترافية. يمكن لأصحاب العمل مراجعة الفيديو قبل جدولة المقابلات الرسمية.'
    },
    {
      question: 'ماذا لو لم ينجح التوظيف؟',
      answer: 'إذا غادر المرشح خلال أول 30 يوماً، سنسعى لتوفير بديل دون تكلفة إضافية، وفقاً لشروط وأحكام الخطة. اتصل بفريقنا للحصول على تفاصيل محددة حول خطتك.'
    },
    {
      question: 'ما مدى أمان بيانات المرشحين؟',
      answer: 'نستخدم التشفير ونلتزم باللائحة العامة لحماية البيانات في المملكة المتحدة وقوانين حماية البيانات السعودية. يتم تخزين جميع البيانات بشكل آمن ولا تتم مشاركتها أبداً دون موافقة صريحة.'
    },
    {
      question: 'هل يمكنني نشر وظائف باللغة العربية؟',
      answer: 'نعم! تدعم منصتنا كلاً من الإنجليزية والعربية. يمكنك نشر الوظائف بأي من اللغتين أو كليهما.'
    },
    {
      question: 'ما الدعم الذي يتلقاه المرشحون للنقل؟',
      answer: 'يتلقى المرشحون دعماً يشمل إرشاد تسجيل SCFHS ومساعدة معالجة التأشيرات والمساعدة في التنسيق لترتيبات السكن والتوجيه الثقافي للعمل في المملكة العربية السعودية.'
    }
  ];

  const additionalFaqs = language === 'ar' ? additionalFaqsAr : additionalFaqsEn;
  const allFaqs = [...faqData, ...additionalFaqs];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('faq.title')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('faq.subtitle')}
            </p>
          </div>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {allFaqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-2 border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-start font-semibold text-gray-900 hover:text-[#42b1c2] py-4" data-testid={`faq-trigger-${index}`}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Still have questions? */}
          <div className="mt-16 bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10 rounded-3xl p-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t('faq.cantFindAnswer')}
            </h2>
            <p className="text-gray-600 mb-6">
              {language === 'ar' ? 'فريقنا هنا للمساعدة. تواصل معنا وسنرد خلال 24 ساعة.' : 'Our team is here to help. Get in touch and we\'ll respond within 24 hours.'}
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white" data-testid="faq-contact-btn">
                {t('faq.contactUs')}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQ;
